import React, { useState, useEffect } from 'react';
import { FaUser, FaCalendarAlt, FaMap } from 'react-icons/fa';
import { FiChevronsDown, FiChevronsUp } from 'react-icons/fi';
import { BsSearch } from 'react-icons/bs';
import { transparentize } from 'polished';
import styled from '@emotion/styled';
import { ConversationCheckAvailabilityReady } from '@tomis-tech/types';

import { useSession, useTyping, useCheckAvailability } from '../context';
import { checkAvailabilityV2 } from '../messages';
import { CheckAvailabilitySearchPrefix } from '../consts';
import {
  abbreviatedDateRange,
  validateCheckAvailabilityParameters,
  isInitialSearch,
  getTourDisplayName,
  createSearchMessage,
} from '../utils';
import { ErrorMessage, Wiggle } from './styled-components';
import { updateConversationCheckAvailability } from '../services/checkAvailability';
import SavingOverlay from './common/SavingOverlay';
import { sendNewCustomerMessage } from '../services/sendNewCustomerMessage';

type Props = {
  /** Function to scroll to bottom of the screen.
   *
   * We need to scroll when check availability is shown */
  scrollToBottom: () => any;
};

const CheckAvailabilityMenu: React.FC<Props> = ({ scrollToBottom }) => {
  /** Necessary hooks for sendNewCustomerMessage function */
  const { conversation, site } = useSession();
  const { setIsTyping } = useTyping();
  const {
    setChangingDates,
    setChangingProduct,
    setChangingQuantity,
    headerErrorMessage,
    setHeaderErrorMessage,
    changingDates,
    changingProduct,
    changingQuantity,
  } = useCheckAvailability();

  /** guaranteed to have checkAvailability object before component mounts */
  const { checkAvailability } = conversation.value;

  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isOpen, setIsOpen] = useState(checkAvailability.searches.length === 0);

  /** Scroll to bottom when check availability opens */
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [isOpen, scrollToBottom]);

  /**
   * Wiggle search button when params change after initial search
   * More accurately, wiggle search button every time one of the parameter
   * screens is closed & the search has not occurred before.
   */
  const [wiggle, setWiggle] = useState(false);
  useEffect(() => {
    const {
      dateTimeEnd,
      dateTimeStart,
      products,
      quantity,
      searches,
    } = checkAvailability;

    if (searches.length > 0) {
      const searchMessage = createSearchMessage(
        products.products,
        dateTimeStart,
        dateTimeEnd,
        quantity
      );

      if (
        !searches.includes(searchMessage) &&
        !changingDates &&
        !changingProduct &&
        !changingQuantity
      ) {
        if (!wiggle) {
          const timerOne = setTimeout(() => setWiggle(true), 1000);
          const timerTwo = setTimeout(() => setWiggle(false), 2500);
          return () => {
            clearTimeout(timerOne);
            clearTimeout(timerTwo);
          };
        }
      }
    }
  }, [checkAvailability, changingDates, changingProduct, changingQuantity]);

  // Automatically Check Availability first time all parameters are valid
  useEffect(() => {
    const { isValid } = validateCheckAvailabilityParameters(checkAvailability);
    if (isValid && isInitialSearch(checkAvailability) && !isLoadingSearch) {
      handleSearch();
    }
  }, [checkAvailability, isLoadingSearch]);

  async function handleSearch() {
    const { isValid, errorMessage } = validateCheckAvailabilityParameters(
      checkAvailability
    );

    if (!isValid) {
      return setHeaderErrorMessage(errorMessage);
    }

    const {
      dateTimeEnd,
      dateTimeStart,
      products,
      quantity,
    } = checkAvailability;

    const searchMessage = createSearchMessage(
      products.products,
      dateTimeStart,
      dateTimeEnd,
      quantity
    );

    setWiggle(false);

    const updatedCheckAvailability: ConversationCheckAvailabilityReady = {
      ...checkAvailability,
    };

    updatedCheckAvailability.searches.push(searchMessage);

    const { error } = await updateConversationCheckAvailability({
      conversation,
      updatedCheckAvailability,
    });

    if (error) {
      return setHeaderErrorMessage('Error searching, try again.');
    } else {
      setHeaderErrorMessage('');
    }

    const customerMessageText = `${CheckAvailabilitySearchPrefix} ${searchMessage}`;

    setIsTyping(true);

    /** Send new customer message (record of search parameters).
     *
     * Not using "async" because this message is just recording the
     * search parameters, it's not vital to Check Availability functionality
     */
    sendNewCustomerMessage({
      text: customerMessageText,
      conversationId: conversation.ref.id,
    });

    // Check availability via Django endpoint
    checkAvailabilityV2({
      checkAvailability,
      setIsLoading: setIsLoadingSearch,
      setIsOpen,
      conversation,
      setIsTyping,
      text: customerMessageText,
    });
  }

  const { primaryColor } = site.botConfig;

  const parameterButtons = [
    {
      icon: FaUser,
      onClick: () => setChangingQuantity(true),
      text:
        checkAvailability.quantity === 0
          ? '(select quantity)'
          : checkAvailability.quantity,
      type: 'quantity',
    },
    {
      icon: FaCalendarAlt,
      onClick: () => setChangingDates(true),
      text:
        checkAvailability.dateTimeEnd && checkAvailability.dateTimeStart
          ? abbreviatedDateRange(
              checkAvailability.dateTimeStart,
              checkAvailability.dateTimeEnd
            )
          : '(select dates)',
      type: 'date',
    },
    {
      icon: FaMap,
      onClick: () => setChangingProduct(true),
      text: getTourDisplayName(checkAvailability.products) || '(select tour)',
      type: 'product',
    },
  ];

  const hideQuantityButton = Boolean(
    site.botConfig.checkAvailability?.fixedQuantity
  );

  /** Remove quantity button from Check Availability menu */
  if (hideQuantityButton) parameterButtons.shift();

  const Chevron = isOpen
    ? () => <FiChevronsDown color="white" size={20} />
    : () => <FiChevronsUp color="white" size={20} />;

  const MainContainer = isOpen ? ContainerOpen : ContainerClosed;
  const AnimationContainer = wiggle ? Wiggle : styled.div();

  return (
    <>
      <div style={{ backgroundColor: 'white', zIndex: 1 }}>
        <MainContainer color={primaryColor}>
          <InsideContainer>
            <ParametersContainer
              onClick={() => !isOpen && setIsOpen(true)}
              aria-checked={isOpen}
            >
              {isOpen &&
                parameterButtons.map((item, index) => (
                  <ParameterButton
                    color={primaryColor}
                    key={index}
                    onClick={item.onClick}
                    style={{
                      marginBottom:
                        parameterButtons.length - 1 === index
                          ? '0px'
                          : undefined,
                    }}
                  >
                    <item.icon style={{ marginRight: '10px' }} />
                    {item.text}
                  </ParameterButton>
                ))}

              {!isOpen && <CollapsedLabel>CHECK AVAILABILITY</CollapsedLabel>}
              {isOpen && headerErrorMessage && (
                <Error>{headerErrorMessage}</Error>
              )}
            </ParametersContainer>
            {isOpen && checkAvailability.searches.length > 0 && (
              <SearchContainer onClick={handleSearch}>
                <AnimationContainer>
                  <SearchButton color={primaryColor}>
                    <BsSearch color="white" size={20} />
                  </SearchButton>
                </AnimationContainer>
                <SearchText>search</SearchText>
              </SearchContainer>
            )}
          </InsideContainer>
          <CollapseOpenContainer onClick={() => setIsOpen(!isOpen)}>
            <ChevronContainer color={isOpen ? primaryColor : undefined}>
              <Chevron />
            </ChevronContainer>
          </CollapseOpenContainer>
        </MainContainer>
      </div>
      {isLoadingSearch && <SavingOverlay text="Checking Availability..." />}
    </>
  );
};

export default CheckAvailabilityMenu;

/** Styled Components */
const CollapseOpenContainer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
`;

const ChevronContainer = styled.div`
  align-items: center;
  background-color: ${props => props.color};
  display: flex;
  justify-content: center;
`;

const CollapsedLabel = styled.p`
  text-align: right;
  font-size: 14px;
  font-style: italic;
`;

const Container = styled.div`
  color: ${props => props.color};
  border-radius: 15px 15px 0px 0px;
  box-shadow: 0px 0px 25px -15px black;
  display: flex;
  margin: 0px 5px;
`;

const ContainerClosed = styled(Container)`
  background-color: ${props => transparentize(0.25, props.color)};
  cursor: pointer;
  flex-direction: row;
  justify-content: flex-end;
  padding: 3px;
`;

const ContainerOpen = styled(Container)`
  background-color: ${props => transparentize(0.8, props.color)};
  flex-direction: column-reverse;
  overflow: hidden;
`;

const Error = styled(ErrorMessage)`
  color: black;
  font-size: 14px;
  margin: 5px 0px 0px 0px;
`;

const InsideContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
`;

const ParametersContainer = styled.div`
  color: white;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-wrap: wrap;
  justify-content: space-evenly;
  margin-right: ${props => (props['aria-checked'] ? '0px' : '10px')};
  opacity: 1;
  padding: ${props => (props['aria-checked'] ? '5px 15px' : '0px')};
`;

const ParameterButton = styled.button`
  align-items: center;
  background: ${props => props.color};
  border: none;
  border-radius: 15px;
  box-shadow: 0px 0px 8px -3px black;
  color: white;
  cursor: pointer;
  display: flex;
  height: fit-content;
  margin-bottom: 5px;
  margin-left: 2px;
  margin-right: 2px;
  opacity: 1;
  padding: 5px 10px;
  width: fit-content;
  &:focus {
    outline: none;
  }
  &:hover {
    transform: scale(1.01);
    background: ${props => transparentize(0.25, props.color)};
  }
`;

const SearchButton = styled.div`
  align-items: center;
  background-color: ${props => props.color};
  border-radius: 100%;
  box-shadow: 0px 0px 8px -3px black;
  display: flex;
  justify-content: center;
  padding: 7px;
  width: fit-content;
`;

const SearchContainer = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 10px;
  max-width: 50px;
  padding: 2px;
`;

const SearchText = styled.p`
  color: black;
  font-size: 12px;
  font-style: italic;
  margin-top: 5px;
  text-align: center;
`;
